import React, { useContext, useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import { Box, Divider, Fade, Grid, Paper } from '@mui/material';
import * as yup from 'yup';
import { postData, getData, putData } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { setYupLocaleES } from '../../const/yup';
import { useNavigate } from 'react-router-dom';
import { CustomForm } from '../../components/formSections';
import ProcessGuide from '../../components/orden/ProcessGuide';
import { GlobalsContext } from '../../context/globals';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import GeoDireccionForm, {
    geoDireccionFromApi,
    geoDireccionToApi,
} from '../../components/geo/GeoDireccionForm';

dayjs.extend(customParseFormat);

setYupLocaleES();

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

function geoStarted(geo) {
    if (!geo) return false;
    return Boolean(
        geo.geo_localidad_id
        || geo.calle_texto
        || geo.altura
        || geo.piso
        || geo.depto
        || geo.referencia
        || geo.geo_calle_id
        || geo.latitud != null
        || geo.longitud != null
    );
}

function Page() {
    const { classes } = useStyles();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const [data, setData] = useState(null);
    const [geoState, setGeoState] = useState(null);

    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = async (values) => {
        let geo_direccion = null;
        if (geoState === null || !geoStarted(geoState)) {
            geo_direccion = null;
        } else {
            geo_direccion = geoDireccionToApi(geoState);
            if (!geo_direccion) {
                enqueueSnackbar(
                    'El domicilio está incompleto. Corresponde completarlo (localidad, ubicación en mapa y, si aplica, calle y altura) o quitarlo.',
                    { variant: 'error' }
                );
                return;
            }
        }

        loadingModal.setOpen(true);

        try {
            const celular = (values.celular || '').trim();
            const email = (values.email || '').trim();
            if (!celular && !email) {
                enqueueSnackbar('Indicar al menos un celular o un correo electrónico para poder avisar el turno.', { variant: 'error' });
                loadingModal.setOpen(false);
                return;
            }

            const payload = {
                nombre: values.nombre,
                fecha_nacimiento: (() => {
                    const v = values.fecha_nacimiento;
                    if (!v) return null;
                    const d = dayjs.isDayjs(v) ? v : dayjs(v);
                    return d.isValid() ? d.format('YYYY-MM-DD') : null;
                })(),
                dni: values.dni === '' ? null : values.dni,
                celular: celular || null,
                email: email || null,
                geo_direccion,
            };

            let response;
            if (data && data.id) {
                response = await putData(`pacientes/${data.id}`, payload, usuario.token);
            } else {
                response = await postData('pacientes', payload, usuario.token);
            }

            if (response.error === '') {
                enqueueSnackbar(
                    data ? 'Paciente modificado correctamente' : 'Paciente creado correctamente',
                    { variant: 'success' }
                );
                onClose();
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al guardar el paciente', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const onClose = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onCancel = () => {
        alertModal.showModal(
            'Cancelar',
            'Se perderán todos los cambios. ¿Deseas continuar?',
            () => onClose()
        );
    };

    const idEdit = globalsContext.idEdit;
    const token = usuario?.token;

    const loadData = useCallback(async () => {
        if (!idEdit || !token) return;
        loadingModal.setOpen(true);
        try {
            const response = await getData(`pacientes/${idEdit}`, token);
            if (response.error === '') {
                setData(response.data);
                setGeoState(geoDireccionFromApi(response.data.geo_direccion));
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error al cargar el paciente', { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    }, [idEdit, token]);

    useEffect(() => {
        if (idEdit) {
            loadData();
        }
    }, [idEdit, loadData]);

    const formSections = useMemo(() => [
        {
            name: 'Datos del paciente',
            description: 'Indique al menos un celular o un correo electrónico para poder avisar el turno.',
            fields: [
                {
                    dataName: 'nombre',
                    label: 'Nombre',
                    controlType: 'textField',
                    initialValue: data ? data.nombre : '',
                    validationSchema: yup.string().required('El nombre es requerido').max(255, 'Máximo 255 caracteres'),
                    autoFocus: true,
                },
                {
                    dataName: 'fecha_nacimiento',
                    label: 'Fecha de nacimiento',
                    controlType: 'datePicker',
                    initialValue: data && data.fecha_nacimiento ? data.fecha_nacimiento : '',
                    validationSchema: yup.date().nullable().max(new Date(), 'La fecha no puede ser futura').transform((v) => {
                        if (v === '' || v == null) return null;
                        if (dayjs.isDayjs(v) && v.isValid()) return v.toDate();
                        if (v instanceof Date) return v;
                        const str = String(v);
                        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
                            const d = dayjs(str, 'DD/MM/YYYY');
                            return d.isValid() ? d.toDate() : v;
                        }
                        const d = dayjs(str);
                        return d.isValid() ? d.toDate() : v;
                    }),
                },
                {
                    dataName: 'dni',
                    label: 'DNI',
                    controlType: 'textField',
                    initialValue: data ? data.dni : '',
                    validationSchema: yup.string().nullable().max(20, 'Máximo 20 caracteres'),
                },
                {
                    dataName: 'celular',
                    label: 'Celular',
                    controlType: 'textField',
                    initialValue: data ? (data.celular || '') : '',
                    validationSchema: yup.string().nullable().max(40, 'Máximo 40 caracteres'),
                },
                {
                    dataName: 'email',
                    label: 'Correo electrónico',
                    controlType: 'textField',
                    initialValue: data ? (data.email || '') : '',
                    validationSchema: yup.string().nullable().email('Debe ser un email válido').max(255),
                },
            ],
        },
    ], [data]);

    const formInitialValues = useMemo(() => ({
        nombre: data ? data.nombre : '',
        fecha_nacimiento: data && data.fecha_nacimiento ? data.fecha_nacimiento : '',
        dni: data ? data.dni : '',
        celular: data ? (data.celular || '') : '',
        email: data ? (data.email || '') : '',
    }), [data]);

    return (
        <Fragment>
            <Layout nombrePagina={data ? 'Editar paciente' : 'Nuevo paciente'}>
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <ProcessGuide
                            title="Cómo completar el paciente"
                            steps={[
                                'Indique al menos un celular o un correo para poder avisar el turno.',
                                'La dirección es opcional pero facilita la identificación.',
                            ]}
                        />
                        <Paper className={classes.paper}>
                            <Box sx={{ mb: 3 }}>
                                <GeoDireccionForm
                                    token={usuario.token}
                                    value={geoState}
                                    onChange={setGeoState}
                                    allowClear
                                />
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            <CustomForm
                                key={data ? `pac-${data.id}` : 'pac-new'}
                                formSections={formSections}
                                initialValues={formInitialValues}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                submitButtonText={data ? 'Guardar cambios' : 'Crear paciente'}
                                cancelButtonText="Cancelar"
                                stepperType="vertical"
                                isModal={false}
                                validateOnChange={false}
                            />
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>
        </Fragment>
    );
}

export default Page;
